import { LayoutOutlined } from "@ant-design/icons";
import { Button, Form, Input, Tabs } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isScalarAttrType } from "src/business/attribute";
import { ZClassifier, ZClassifierCard } from "src/business/classifier";
import {
  apiExtensionsToForm,
  ExtensionBindingObjectType,
} from "src/business/extensionBindings";
import { EditRoleAccessList } from "src/business/permissions/EditRoleAccessList";
import {
  SpecificationAttrSelect,
  SpecificationSelect,
} from "src/business/specification";
import { mdmDeepEqual } from "src/common/mdmDeepEqual";
import { onError } from "src/common/onError";
import { maxNameLength } from "src/common/rxName";
import { IdBox } from "src/components/IdBox";
import { LoaderBox } from "src/components/LoaderBox";
import { msgQueueStore } from "src/components/MsgQueue";
import { PageBlocker } from "src/components/PageBlocker";
import { PageLayout } from "src/components/PageLayout";
import { BookIcon } from "src/icons/BookIcon";
import { ObjectCardHeader } from "src/pages/management/objects/common/ObjectCardHeader";
import * as layoutStyles from "src/pages/management/objects/common/ObjectCardLayout.module.less";
import { ObjectCardTabPane } from "src/pages/management/objects/common/ObjectCardTabPane";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { ZSidLabel } from "src/types";

import { SysDataFormItems } from "../../../../../business/sysData/SysDataFormItems";
import { EventsAndExtensionsTab } from "../../common/EventsAndExtensions";
import { ClassifierCardActions } from "./ClassifierCardActions";
import { classifierCardStore as pageStore } from "./classifierCardStore";
import * as styles from "./ManagementClassifierCard.module.less";

export const ManagementClassifierCard: React.FC = observer(() => {
  const { id } = useParams();
  React.useEffect(() => {
    pageStore.init(id ?? "new").catch(onError);
  }, [id]);
  return (
    <PageLayout title={pageStore.crumbs}>
      <LoaderBox
        remoteData={pageStore.data}
        drawReady={(values) => <Inner srcData={values} />}
      />
    </PageLayout>
  );
});

const classifier2card = (cls: ZClassifier): ZClassifierCard => {
  const {
    name,
    description,
    displayAttribute,
    schemas,
    permissions,
    extensions,
  } = cls;
  return {
    name,
    description,
    displayAttributeId: displayAttribute?.id,
    schemas: schemas?.[0],
    permissions,
    extensions: apiExtensionsToForm(extensions),
  };
};

const field = (fieldName: keyof ZClassifierCard): string => fieldName;

enum TabKey {
  General = "general",
  Events = "events",
  SystemData = "systemData",
}

const Inner: React.FC<{ srcData: ZClassifier }> = observer(({ srcData }) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = React.useState<TabKey>(TabKey.General);
  const schemas = Form.useWatch<ZSidLabel | undefined>(field("schemas"), form);
  const specId = schemas?.id;

  const navigate = useNavigate();
  React.useEffect(() => {
    form.resetFields();
    form.setFieldsValue(classifier2card(srcData));
    pageStore.setInitialValues(form.getFieldsValue());
    pageStore.setChanged(false);
  }, [srcData]);

  const onFormChange = (
    changedFields: Record<string, unknown>,
    all: unknown,
  ) => {
    if (changedFields[field("schemas")]) {
      const newSpec = changedFields[field("schemas")] as ZSidLabel;
      if (String(newSpec?.id) !== String(specId)) {
        form.setFieldValue(field("displayAttributeId"), undefined);
      }
    }
    // MobX обворачивает данные в Proxy, при этом результаты сравнения могут быть неправильные.
    const a = JSON.parse(JSON.stringify(all));
    const b = JSON.parse(JSON.stringify(pageStore.initialValues));
    pageStore.setChanged(!mdmDeepEqual(a, b));
  };

  const deleteTask = async () => {
    await pageStore.delete(srcData.id);
    navigate(makeUrl(PageUrl.managementClassifiers), { replace: true });
  };

  const onFinish = () => {
    const values = form.getFieldsValue(true) as ZClassifierCard;
    if (pageStore.isNew) {
      pageStore
        .create(values)
        .then(({ id }) => {
          msgQueueStore.add({
            type: "success",
            message: "Создан новый классификатор",
          });
          // редирект на страницу с id созданного объекта
          navigate(makeUrl(PageUrl.managementClassifierCard, { id }), {
            replace: true,
          });
        })
        .catch((error) => {
          setActiveTab(TabKey.General);
          onError(error);
        });
    } else {
      pageStore
        .save(values)
        .then(() =>
          msgQueueStore.add({ type: "info", message: "Изменения сохранены" }),
        )
        .catch((error) => {
          setActiveTab(TabKey.General);
          onError(error);
        });
    }
  };

  const tabsItems = React.useMemo(() => {
    const items = [{ key: TabKey.General, label: "Общее" }];
    if (!pageStore.isNew) {
      items.push({ key: TabKey.Events, label: "События и расширения" });
      items.push({ key: TabKey.SystemData, label: "Системные данные" });
    }
    return items;
  }, [pageStore.isNew]);

  return (
    <Form<ZClassifierCard>
      layout="vertical"
      form={form}
      className={layoutStyles.formColumnRoot}
      onFinish={onFinish}
      onValuesChange={onFormChange}
      onFinishFailed={() => setActiveTab(TabKey.General)}
    >
      <PageBlocker changed={() => pageStore.changed} />
      <ObjectCardHeader
        title={pageStore.title}
        submitText={pageStore.isNew ? "Создать" : "Сохранить"}
        submitLoading={pageStore.saving}
        submitHtmlType="submit"
      >
        {!pageStore.isNew && (
          <>
            {pageStore.changed && "*"}
            <Link to={makeUrl(PageUrl.classes, { id: srcData.id })}>
              <Button
                type="text"
                icon={<BookIcon />}
                className={styles.classesLink}
              >
                К списку классов
              </Button>
            </Link>
            <ClassifierCardActions deleteTask={deleteTask} />
            <Link
              to={makeUrl(PageUrl.managementClassifierAccess, {
                id: srcData.id,
              })}
            >
              <Button icon={<LayoutOutlined />}>Настроить представление</Button>
            </Link>
          </>
        )}
      </ObjectCardHeader>

      <div className={layoutStyles.tabsContainer}>
        <Tabs
          items={tabsItems}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TabKey)}
        />
        <div className={layoutStyles.tabContent}>
          <ObjectCardTabPane
            className={layoutStyles.commonBlock}
            hidden={activeTab !== TabKey.General}
            hiddenClassName={layoutStyles.hidden}
          >
            <Form.Item
              name={field("name")}
              label="Название"
              rules={[{ required: true }, { max: maxNameLength }]}
            >
              <Input
                allowClear
                disabled={!pageStore.isNew}
                maxLength={maxNameLength}
              />
            </Form.Item>
            <IdBox entity={srcData} />
            <Form.Item name={field("description")} label="Описание">
              <Input.TextArea allowClear />
            </Form.Item>
            <Form.Item
              name={field("schemas")}
              label="Первичная спецификация"
              rules={[
                {
                  required: true,
                  message: "Первичная спецификация обязательна",
                },
              ]}
            >
              <SpecificationSelect
                specType="primary"
                disabled={!pageStore.isNew}
              />
            </Form.Item>
            <Form.Item
              name={field("displayAttributeId")}
              label="Атрибут для отображения"
              rules={[
                {
                  required: true,
                  message: "Атрибут для отображения обязателен",
                },
              ]}
            >
              <SpecificationAttrSelect
                specId={specId}
                allowClear
                filterFn={isScalarAttrType}
                disabled={!specId}
              />
            </Form.Item>
            <Form.Item name={field("permissions")}>
              <EditRoleAccessList />
            </Form.Item>
          </ObjectCardTabPane>
          <ObjectCardTabPane
            className={layoutStyles.tabPaneInset}
            hidden={activeTab !== TabKey.Events}
            hiddenClassName={layoutStyles.hidden}
          >
            <Form.Item name={field("extensions")}>
              <EventsAndExtensionsTab
                scope={ExtensionBindingObjectType.Classifier}
              />
            </Form.Item>
          </ObjectCardTabPane>
          {!pageStore.isNew && (
            <ObjectCardTabPane
              className={layoutStyles.commonBlock}
              hidden={activeTab !== TabKey.SystemData}
              hiddenClassName={layoutStyles.hidden}
            >
              <SysDataFormItems data={srcData} />
            </ObjectCardTabPane>
          )}
        </div>
      </div>
    </Form>
  );
});
